import { useMemo } from 'react';

import { normalizeForMatch } from '../utils/normalizeForMatch';
import type { ResolvedSkill } from './useMapUserSkills';
import type { RoleSkill, RoleSkillTimepoint } from './useRoleSkillComposition';

export type SkillTrend = 'emerging' | 'stable' | 'commoditized';

export interface MissingSkill {
  id: number;
  shortName: string;
  longName: string;
  roleShare: number;
  trend: SkillTrend;
  trendChangePercent: number;
  timeseries: RoleSkillTimepoint[];
}

export interface MatchedSkill {
  id: number;
  shortName: string;
  longName: string;
  roleShare: number;
}

export interface UseSkillGapResult {
  missingSkills: MissingSkill[];
  matchedSkills: MatchedSkill[];
}

const MAX_MISSING_SKILLS = 8;

const classifySkillTrend = (
  timeseries: Array<{ id: number; share: number }>
): { trend: SkillTrend; changePercent: number } => {
  if (timeseries.length < 6) return { trend: 'stable', changePercent: 0 };

  const recent = timeseries.slice(-6);
  const earlier = timeseries.slice(0, 6);

  const avgRecent = recent.reduce((s, p) => s + p.share, 0) / recent.length;
  const avgEarlier = earlier.reduce((s, p) => s + p.share, 0) / earlier.length;

  if (avgEarlier === 0) return { trend: 'stable', changePercent: 0 };

  const changePercent = ((avgRecent - avgEarlier) / avgEarlier) * 100;

  if (changePercent > 15) return { trend: 'emerging', changePercent };
  if (changePercent < -10) return { trend: 'commoditized', changePercent };
  return { trend: 'stable', changePercent };
};

export const useSkillGap = (
  roleSkills: RoleSkill[],
  resolvedUserSkills: ResolvedSkill[]
): UseSkillGapResult => {
  return useMemo(() => {
    if (roleSkills.length === 0) {
      return { missingSkills: [], matchedSkills: [] };
    }

    // Build a set of normalized user skill names for matching
    const userSkillNames = new Set(
      resolvedUserSkills.map((s) => normalizeForMatch(s.taxonomyName))
    );

    // Also include the original user-provided names (in case taxonomy names differ)
    resolvedUserSkills.forEach((s) => {
      userSkillNames.add(normalizeForMatch(s.name));
    });

    const missingSkills: MissingSkill[] = [];
    const matchedSkills: MatchedSkill[] = [];

    for (const roleSkill of roleSkills) {
      const normalizedShort = normalizeForMatch(roleSkill.shortName);
      const normalizedLong = normalizeForMatch(roleSkill.longName);

      const isMatched =
        userSkillNames.has(normalizedShort) ||
        userSkillNames.has(normalizedLong);

      if (isMatched) {
        matchedSkills.push({
          id: roleSkill.id,
          shortName: roleSkill.shortName,
          longName: roleSkill.longName,
          roleShare: roleSkill.latestShare,
        });
      } else {
        const { trend, changePercent } = classifySkillTrend(
          roleSkill.timeseries
        );

        missingSkills.push({
          id: roleSkill.id,
          shortName: roleSkill.shortName,
          longName: roleSkill.longName,
          roleShare: roleSkill.latestShare,
          trend,
          trendChangePercent: changePercent,
          timeseries: roleSkill.timeseries,
        });
      }
    }

    // Sort missing by role share (most common first), limit to top N
    missingSkills.sort((a, b) => b.roleShare - a.roleShare);

    return {
      missingSkills: missingSkills.slice(0, MAX_MISSING_SKILLS),
      matchedSkills,
    };
  }, [roleSkills, resolvedUserSkills]);
};
